import React, { useState, useRef, useCallback, useEffect } from 'react'
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    DialogContentText,
    Button,
    IconButton,
    TextField,
    Slider,
    Typography,
} from '@material-ui/core'
import { Delete } from '@material-ui/icons'
import Edit from '@material-ui/icons/Edit'
import ImageIcon from '@material-ui/icons/Image'
import Cropper from 'react-easy-crop'
import { getCroppedImg } from '../../../utils/cropUtils'
import EmailPreview from './emailPreview'
import { useTheme } from '@material-ui/core/styles'
import { strings } from '../../../localization/strings'

var OUTPUT_LOGO_WIDTH = 140
var OUTPUT_LOGO_HEIGHT = 60
var LOGO_ASPECT = OUTPUT_LOGO_WIDTH / OUTPUT_LOGO_HEIGHT
var CROP_DISPLAY_WIDTH = 500
var CROP_DISPLAY_HEIGHT = Math.round(CROP_DISPLAY_WIDTH * OUTPUT_LOGO_HEIGHT / OUTPUT_LOGO_WIDTH)
const emailStrings = strings.configNotificationTemplateEmail

var SectionHeader = function ({ children }) {
    var theme = useTheme()
    return (
        <Typography
            variant="overline"
            component="div"
            style={{
                fontSize: 11,
                letterSpacing: '0.9px',
                color: theme.palette.primary.main,
                paddingBottom: 8,
                marginBottom: 14,
                borderBottom: `1px solid ${theme.palette.primary.main}`,
                lineHeight: 1.4,
            }}
        >
            {children}
        </Typography>
    )
}

var checkerboardStyle = {
    backgroundColor: '#ffffff',
    backgroundImage: [
        'linear-gradient(45deg, #ebebeb 25%, transparent 25%)',
        'linear-gradient(-45deg, #ebebeb 25%, transparent 25%)',
        'linear-gradient(45deg, transparent 75%, #ebebeb 75%)',
        'linear-gradient(-45deg, transparent 75%, #ebebeb 75%)',
    ].join(', '),
    backgroundSize: '16px 16px',
    backgroundPosition: '0 0, 0 8px, 8px -8px, -8px 0px',
}

var LogoUploadArea = function ({ src, onClick, onRemove }) {
    var [hovered, setHovered] = useState(false)
    var [emptyHovered, setEmptyHovered] = useState(false)
    var [showDeleteConfirm, setShowDeleteConfirm] = useState(false)

    var handleDeleteClick = function (e) {
        e.stopPropagation()
        setShowDeleteConfirm(true)
    }
    if (!src) {
        return (
            <div
                onClick={onClick}
                onMouseEnter={function () { setEmptyHovered(true) }}
                onMouseLeave={function () { setEmptyHovered(false) }}
                style={{
                    width: '100%',
                    height: 104,
                    border: emptyHovered ? '2px dashed #1976d2' : '2px dashed #d0d0d0',
                    borderRadius: 6,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    background: emptyHovered ? '#f0f4ff' : '#fafafa',
                    transition: 'border-color 0.15s, background 0.15s',
                    userSelect: 'none',
                    gap: 4,
                }}
            >
                <ImageIcon
                    style={{
                        fontSize: 30,
                        color: emptyHovered ? '#1976d2' : '#c0c0c0',
                        transition: 'color 0.15s',
                        marginBottom: 2,
                    }}
                />
                <Typography
                    variant="body2"
                    style={{
                        color: emptyHovered ? '#1565c0' : '#757575',
                        fontWeight: 500,
                        transition: 'color 0.15s',
                    }}
                >
                    {emailStrings.uploadLogo}
                </Typography>
                <Typography variant="caption" style={{ color: '#bdbdbd' }}>
                    {emailStrings.logoFormats}
                </Typography>
            </div>
        )
    }

    return (
        <div>
            <div
                style={{
                    width: '100%',
                    height: 104,
                    borderRadius: 6,
                    border: '1px solid #e0e0e0',
                    position: 'relative',
                    overflow: 'hidden',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    ...checkerboardStyle,
                }}
                onMouseEnter={function () { setHovered(true) }}
                onMouseLeave={function () { setHovered(false) }}
            >
                <img
                    src={src}
                    alt="Logo"
                    style={{
                        maxHeight: 84,
                        maxWidth: '90%',
                        objectFit: 'contain',
                        display: 'block',
                    }}
                />

                {hovered && (
                    <div
                        style={{
                            position: 'absolute',
                            inset: 0,
                            background: 'rgba(0,0,0,0.42)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                        onClick={function (e) { e.stopPropagation() }}
                    >
                        {showDeleteConfirm ? (
                            <div
                                style={{
                                    background: '#ffffff',
                                    borderRadius: 8,
                                    padding: '12px 16px',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    gap: 12,
                                    boxShadow: '0 2px 8px rgba(0,0,0,0.25)',
                                }}
                            >
                                <Typography
                                    variant="body2"
                                    style={{
                                        color: '#424242',
                                        fontWeight: 500,
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    {emailStrings.deleteLogo}
                                </Typography>

                                <div
                                    style={{
                                        display: 'flex',
                                        gap: 8,
                                    }}
                                >
                                    <Button
                                        size="small"
                                        onClick={function () {
                                            setShowDeleteConfirm(false)
                                        }}
                                    >
                                        {emailStrings.cancel}
                                    </Button>

                                    <Button
                                        size="small"
                                        variant="contained"
                                        color='primary'
                                        style={{
                                            color: '#ffffff',
                                        }}
                                        onClick={function () {
                                            setShowDeleteConfirm(false)
                                            onRemove()
                                        }}
                                    >
                                        {emailStrings.delete}
                                    </Button>
                                </div>
                            </div>
                        ) : (
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 20,
                                }}>
                                <IconButton
                                    size="small"
                                    onClick={onClick}
                                    title={emailStrings.changeLogo}
                                    style={{
                                        width: 40,
                                        height: 40,
                                        color: '#1976d2',
                                        background: '#ffffff',
                                        border: '1px solid #1976d2',
                                        boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                                    }}
                                >
                                    <Edit style={{ fontSize: 21 }} />
                                </IconButton>

                                <IconButton
                                    size="small"
                                    onClick={handleDeleteClick}
                                    title={emailStrings.deleteLogo}
                                    style={{
                                        width: 40,
                                        height: 40,
                                        color: '#d32f2f',
                                        background: '#ffffff',
                                        border: '1px solid #d32f2f',
                                        boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                                    }}
                                >
                                    <Delete style={{ fontSize: 21 }} />
                                </IconButton>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    )
}

var ColorPickerField = function ({ value, onChange }) {
    var colorInputRef = useRef(null)
    var [hexInput, setHexInput] = useState(value || '')

    useEffect(function () {
        setHexInput(value || '')
    }, [value])

    var isValidHex = function (str) {
        return /^#[0-9a-fA-F]{6}$/.test(str)
    }

    var hasColor = isValidHex(hexInput)

    var handleHexChange = function (e) {
        var val = e.target.value
        setHexInput(val)
        if (isValidHex(val)) onChange(val)
    }

    var handleHexBlur = function () {
        if (hexInput && !isValidHex(hexInput)) setHexInput('')
    }

    var swatchBackground = hasColor
        ? hexInput
        : 'linear-gradient(to bottom right, #e0e0e0 calc(50% - 1px), #9e9e9e calc(50% - 1px), #9e9e9e calc(50% + 1px), #e0e0e0 calc(50% + 1px))'

    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div
                onClick={function () { colorInputRef.current && colorInputRef.current.click() }}
                title={hasColor ? emailStrings.changeColor : emailStrings.selectColor}
                style={{
                    width: 32,
                    height: 32,
                    borderRadius: 4,
                    border: '1px solid rgba(0,0,0,0.23)',
                    background: swatchBackground,
                    cursor: 'pointer',
                    flexShrink: 0,
                    boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
                    position: 'relative',
                    overflow: 'hidden',
                }}
            >
                <input
                    ref={colorInputRef}
                    type="color"
                    value={hasColor ? hexInput : '#1155cc'}
                    onChange={function (e) {
                        setHexInput(e.target.value)
                        onChange(e.target.value)
                    }}
                    style={{
                        position: 'absolute',
                        top: -8,
                        left: -8,
                        width: 'calc(100% + 16px)',
                        height: 'calc(100% + 16px)',
                        border: 'none',
                        padding: 0,
                        cursor: 'pointer',
                        opacity: 0,
                    }}
                />
            </div>
            <TextField
                value={hexInput}
                onChange={handleHexChange}
                onBlur={handleHexBlur}
                variant="outlined"
                placeholder="#000000"
                inputProps={{
                    maxLength: 7,
                    style: { fontFamily: 'monospace', fontSize: 13, padding: '7px 10px' },
                }}
                style={{ width: 110 }}
                error={hexInput.length > 0 && !isValidHex(hexInput)}
            />
        </div>
    )
}

var DiscardConfirmDialog = function ({ onConfirm, onCancel }) {
    return (
        <Dialog open onClose={onCancel} maxWidth="xs">
            <DialogTitle>{emailStrings.discardChangesTitle}</DialogTitle>
            <DialogContent>
                <DialogContentText>
                    {emailStrings.discardChangesMessage}
                </DialogContentText>
            </DialogContent>
            <DialogActions>
                <Button variant="outlined" onClick={onCancel}>
                    {emailStrings.continueEditing}
                </Button>
                <Button
                    variant="contained"
                    color='primary'
                    onClick={onConfirm}
                    style={{ color: '#fff' }}
                >
                    {emailStrings.discardChanges}
                </Button>
            </DialogActions>
        </Dialog>
    )
}

var ImageCropDialog = function ({ imageSrc, onConfirm, onCancel }) {
    var [ready, setReady] = useState(false)
    var [crop, setCrop] = useState({ x: 0, y: 0 })
    var [zoom, setZoom] = useState(1)
    var [minZoom, setMinZoom] = useState(1)
    var [croppedAreaPixels, setCroppedAreaPixels] = useState(null)
    var [applying, setApplying] = useState(false)
    var [error, setError] = useState(null)
    var imageRef = useRef(null)

    useEffect(function () {
        var cancelled = false
        var img = new Image()
        imageRef.current = img
        img.onload = function () {
            if (cancelled) return
            var imageAR = img.naturalWidth / img.naturalHeight
            var newMinZoom = Math.max(
                0.1,
                Math.min(1, imageAR / LOGO_ASPECT)
            )
            setMinZoom(newMinZoom)
            setZoom(newMinZoom)
            setReady(true)
        }
        img.onerror = function () {
            if (!cancelled) {
                setError(emailStrings.cropImageError)
            }
        }
        img.src = imageSrc

        return function () {
            cancelled = true
            if (imageRef.current) {
                imageRef.current.onload = null
                imageRef.current.onerror = null
                imageRef.current.src = ''
            }
        }
    }, [imageSrc])

    var handleCropComplete = useCallback(function (_, pixels) {
        setCroppedAreaPixels(pixels)
    }, [])

    var handleConfirm = async function () {
        if (!croppedAreaPixels || applying) return
        setError(null)
        setApplying(true)
        try {
            var base64 = await getCroppedImg(
                imageSrc,
                croppedAreaPixels
            )
            onConfirm(base64)
        } catch (err) {
            setError(emailStrings.cropImageError)
            setApplying(false)
        }
    }

    return (
        <Dialog open onClose={onCancel} fullWidth maxWidth="sm">
            <DialogTitle>{emailStrings.cropLogo}</DialogTitle>
            <DialogContent>
                <div
                    style={{
                        position: 'relative',
                        height: CROP_DISPLAY_HEIGHT + 56,
                        background: '#f0f0f0',
                        borderRadius: 4,
                    }}
                >
                    {ready && (
                        <Cropper
                            image={imageSrc}
                            crop={crop}
                            zoom={zoom}
                            minZoom={minZoom}
                            aspect={LOGO_ASPECT}
                            cropSize={{width: CROP_DISPLAY_WIDTH, height: CROP_DISPLAY_HEIGHT}}
                            restrictPosition={false}
                            onCropChange={setCrop}
                            onZoomChange={setZoom}
                            onCropComplete={handleCropComplete}
                        />
                    )}
                </div>
                <div style={{
                        marginTop: 16,
                        paddingLeft: 8,
                        paddingRight: 8,
                    }}
                >
                    <Typography
                        variant="caption"
                        color="textSecondary"
                    >
                        {emailStrings.zoom}
                    </Typography>
                    <Slider
                        value={zoom}
                        min={minZoom}
                        max={3}
                        step={0.01}
                        onChange={function (_, value) { setZoom(value)}}
                        aria-label="Zoom"
                    />
                </div>
                <Typography
                    variant="caption"
                    color="textSecondary"
                >
                    {emailStrings.cropLogoHelp}
                </Typography>
                {error && (
                    <Typography
                        variant="body2"
                        color="error"
                        style={{ marginTop: 8 }}
                    >
                        {error}
                    </Typography>
                )}
            </DialogContent>
            <DialogActions>
                <Button
                    onClick={onCancel}
                    disabled={applying}
                >
                    {emailStrings.cancel}
                </Button>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleConfirm}
                    disabled={applying || !croppedAreaPixels}
                >
                    {applying
                        ? emailStrings.processing
                        : emailStrings.applyCrop}
                </Button>
            </DialogActions>
        </Dialog>
    )
}

var EmailTemplateCustomizer = function ({ open, onClose, config, handleEdit }) {
    var [localConfig, setLocalConfig] = useState(config)
    var [isDirty, setIsDirty] = useState(false)
    var [showDiscardConfirm, setShowDiscardConfirm] = useState(false)
    var [pendingImageSrc, setPendingImageSrc] = useState(null)
    var fileInputRef = useRef(null)

    useEffect(function () {
        if (open) {
            setLocalConfig(config)
            setIsDirty(false)
            setShowDiscardConfirm(false)
            setPendingImageSrc(null)
        }
    }, [open]) // eslint-disable-line react-hooks/exhaustive-deps

    var updateLocalField = function (field, value) {
        setLocalConfig(function (prev) {
            return Object.assign({}, prev, { [field]: value })
        })
        setIsDirty(true)
    }

    var handleConfirm = function () {
        handleEdit({ wasEdited: true, config: localConfig })
        onClose()
    }

    var handleRequestClose = function () {
        if (isDirty) {
            setShowDiscardConfirm(true)
        } else {
            onClose()
        }
    }

    var handleDiscardConfirmed = function () {
        setShowDiscardConfirm(false)
        onClose()
    }

    var handleOpenFile = function () {
        if (fileInputRef.current) {
            fileInputRef.current.value = ''
            fileInputRef.current.click()
        }
    }

    var handleFileSelected = function (e) {
        var file = e.target.files && e.target.files[0]
        if (!file) return

        if (!file.type.startsWith('image/')) {
            window.showDialog
                ? window.showDialog({ title: emailStrings.invalidFileTitle, message: emailStrings.invalidFileMessage })
                : alert(emailStrings.onlyImagesAllowed)
            return
        }

        var reader = new FileReader()
        reader.onload = function () { setPendingImageSrc(reader.result) }
        reader.onerror = function () {
            window.showDialog
                ? window.showDialog({ title: emailStrings.error, message: emailStrings.fieldReadError })
                : alert(emailStrings.fieldReadError)
        }
        reader.readAsDataURL(file)
    }

    var handleCropConfirm = function (base64) {
        setPendingImageSrc(null)
        updateLocalField('notificationTemplateLogo', base64)
    }

    var handleCropCancel = function () {
        setPendingImageSrc(null)
    }

    var handleRemoveLogo = function () {
        updateLocalField('notificationTemplateLogo', null)
    }

    return (
        <>
            <Dialog open={open} onClose={handleRequestClose} fullWidth maxWidth="lg">
                <DialogTitle disableTypography>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <Typography variant="h6">{emailStrings.emailCustomization}</Typography>
                        {isDirty && (
                            <span
                                style={{
                                    fontSize: 11,
                                    fontWeight: 500,
                                    color: '#e65100',
                                    background: '#fff3e0',
                                    padding: '2px 8px',
                                    borderRadius: 10,
                                    letterSpacing: '0.3px',
                                }}
                            >
                                {emailStrings.unsavedChanges}
                            </span>
                        )}
                    </div>
                </DialogTitle>

                <DialogContent style={{ display: 'flex', gap: 24, alignItems: 'stretch' }}>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        onChange={handleFileSelected}
                        style={{ display: 'none' }}
                    />
                    <div style={{ width: 300, flexShrink: 0 }}>
                        <SectionHeader>{emailStrings.brand}</SectionHeader>
                        <div style={{ marginBottom: 20 }}>
                            <Typography
                                variant="body2"
                                style={{ marginBottom: 8, fontWeight: 500, color: '#424242' }}
                            >
                                {emailStrings.logo}
                            </Typography>
                            <LogoUploadArea
                                src={localConfig.notificationTemplateLogo}
                                onClick={handleOpenFile}
                                onRemove={handleRemoveLogo}
                            />
                        </div>
                        <div style={{ marginBottom: 28 }}>
                            <Typography
                                variant="body2"
                                style={{ marginBottom: 8, fontWeight: 500, color: '#424242' }}
                            >
                                {emailStrings.primaryColor}
                            </Typography>
                            <ColorPickerField
                                value={localConfig.notificationTemplateColor || ''}
                                onChange={function (val) {
                                    updateLocalField('notificationTemplateColor', val)
                                }}
                            />
                        </div>
                        <SectionHeader>{emailStrings.email}</SectionHeader>
                        <div>
                            <Typography
                                variant="body2"
                                style={{ marginBottom: 8, fontWeight: 500, color: '#424242' }}
                            >
                                {emailStrings.subjectPrefix}
                            </Typography>
                            <TextField
                                fullWidth
                                variant="outlined"
                                value={localConfig.notificationTemplateSubjectPrefix || ''}
                                onChange={function (e) {
                                    updateLocalField('notificationTemplateSubjectPrefix', e.target.value)
                                }}
                                placeholder={emailStrings.subjectPrefixPlaceholder}
                                helperText={emailStrings.subjectPrefixHelper}
                                inputProps={{ style: { padding: '9px 12px' } }}
                            />
                        </div>
                    </div>
                    <div
                        style={{
                            flex: 1,
                            border: '1px solid #e0e0e0',
                            borderRadius: 6,
                            overflow: 'hidden',
                            display: 'flex',
                            flexDirection: 'column',
                        }}
                    >
                        <div
                            style={{
                                padding: '8px 16px',
                                background: '#f5f5f5',
                                borderBottom: '1px solid #e0e0e0',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 12,
                            }}
                        >
                            <Typography
                                variant="caption"
                                style={{ color: '#757575', fontWeight: 500, letterSpacing: '0.2px', whiteSpace: 'nowrap' }}
                            >
                                {emailStrings.emailPreview}
                            </Typography>
                            <div
                                style={{
                                    flex: 1,
                                    minWidth: 0,
                                    background: '#ffffff',
                                    border: '1px solid #e0e0e0',
                                    borderRadius: 4,
                                    padding: '3px 10px',
                                    display: 'flex',
                                    alignItems: 'baseline',
                                    gap: 5,
                                    overflow: 'hidden',
                                }}
                            >
                                <Typography variant="caption" style={{ color: '#9e9e9e', whiteSpace: 'nowrap' }}>
                                    {emailStrings.subject}
                                </Typography>
                                <Typography variant="caption" style={{ color: '#212121', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {localConfig.notificationTemplateSubjectPrefix
                                        ? localConfig.notificationTemplateSubjectPrefix + ` - ${emailStrings.confirmedReservationSubject}`
                                        : emailStrings.confirmedReservationSubject}
                                </Typography>
                            </div>
                        </div>
                        <div style={{ flex: 1, overflowY: 'auto', height: 580 }}>
                            <EmailPreview
                                logo={localConfig.notificationTemplateLogo}
                                brandColor={localConfig.notificationTemplateColor}
                            />
                        </div>
                    </div>
                </DialogContent>

                <DialogActions>
                    <Button onClick={handleRequestClose}>Cancelar</Button>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleConfirm}
                        disabled={!isDirty}
                        style={{
                            color: '#ffffff',
                        }}
                    >
                        {emailStrings.accept}
                    </Button>
                </DialogActions>
            </Dialog>

            {showDiscardConfirm && (
                <DiscardConfirmDialog
                    onConfirm={handleDiscardConfirmed}
                    onCancel={function () { setShowDiscardConfirm(false) }}
                />
            )}

            {pendingImageSrc && (
                <ImageCropDialog
                    imageSrc={pendingImageSrc}
                    onConfirm={handleCropConfirm}
                    onCancel={handleCropCancel}
                />
            )}
        </>
    )
}

export default EmailTemplateCustomizer